/**
 * Is the active tenant identity-only? The MOBILE shell's one derivation (§8).
 *
 * `MobileShell` mounts its own tree and fetches `my-tenants` itself, so it
 * cannot read the desktop shell's `identityOnly` — hence a hook rather than a
 * prop threaded from a common parent, of which there is none.
 *
 * It reuses the SAME TanStack query key every mobile screen already uses
 * (`['mobile-my-tenants']`, 60 s staleTime), so this adds no request: six
 * screens already subscribe to that cache, and a seventh reader is free.
 *
 * The RULE is not re-derived — `isIdentityOnlyTenant` from `@papan/orgs/client`
 * is the same predicate the desktop shell uses, reading the same server-resolved
 * field. Nothing here re-implements the override → default → 'full' chain.
 *
 * Absent `mode` is FULL, deliberately: this is disclosure, not enforcement (see
 * `isIdentityOnlyTenant`), and treating an older API's omission as identity-only
 * would strip the drive from every phone during a rolling restart.
 */
import { useQuery } from '@tanstack/react-query'
import { orgsApi, isIdentityOnlyTenant } from '@papan/orgs/client'
import { useWriteStatus } from '@papan/core-ui/ui'
import { useTenant } from '../hooks/useTenant'

export function useIdentityOnly(): boolean {
  const { tenantId } = useTenant()
  const { data } = useQuery({
    queryKey: ['mobile-my-tenants'],
    queryFn: () => orgsApi.listMyTenants(),
    staleTime: 60_000,
  })
  // OR'd with the polled write-status read, exactly as desktop does — see the
  // note in App.tsx. `my-tenants` covers every tenant (the switcher needs that)
  // but is fetched on tenant change; write-status covers only the active one
  // and is polled, so it is what notices a platform admin's mid-session change.
  // Same server-resolved field through the same resolver, so they differ only
  // in freshness.
  const writeStatus = useWriteStatus()
  return isIdentityOnlyTenant(data?.tenants.find(t => t.id === tenantId))
    || writeStatus.identityOnly
}
